import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseRadioGroup } from '@mgm/common/components/base';
import { NumericField } from '../../../../components/form';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getSelectedCommissionPlan, getSelectedProduct } from '../../../../store/commissions/selectors';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { useCommissions } from '../../../../hooks/useCommissions';

type TCreateFixedInput = {
  handleFormChange: (key: string, val: number | string) => void
  isCommissionPerProduct?: boolean
};

const CreateFixedInput: FC<TCreateFixedInput> = ({ handleFormChange, isCommissionPerProduct = false }) => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);
  const selectedProduct = useAppSelector(getSelectedProduct);
  const { t } = useTranslation(['team', 'commissions', 'common']);
  const getCurrency = useAppSelector(getCurrencyByCode);

  const { fixedTypesOptions } = useCommissions();

  const formValue = useMemo(() => (selectedCommissionPlan?.type === 'Products' ? selectedProduct.fixedType : selectedCommissionPlan.fixedType), [selectedCommissionPlan, selectedProduct]);

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        {...(isMobile && {
          paddingX: 0,
        })}
        size={{
          xs: 12, lg: 5, md: 5, sm: 5,
        }}
        paddingTop={0}
      >
        <BaseRadioGroup
          changeValue={(type) => handleFormChange('fixedType', Number(type))}
          value={formValue}
          label={t('type', { ns: 'common' })}
          items={fixedTypesOptions}
          isRequired
        />
      </Grid>
      <Grid
        {...(isMobile && {
          paddingX: 0,
        })}
        paddingTop={isMobile ? 0 : 2}
        size={{
          xs: 8, lg: 5, md: 5, sm: 5,
        }}
      >
        {formValue === 0 ? (
          <NumericField
            name="fixedAmount"
            borderRadius={1}
            allowRange={[1, Infinity]}
            onChange={(val) => handleFormChange('fixedAmount', val)}
            adornment={getCurrency().mark}
            placeholder={t('fixedAmount', { ns: 'commissions' })}
          />
        ) : (
          <NumericField
            name="fixedAmount"
            onChange={(val) => handleFormChange('fixedAmount', val)}
            placeholder={t('fixedPercent', { ns: 'commissions' })}
            borderRadius={1}
            allowRange={[0.1, 100]}
            adornment="%"
          />
        )}
      </Grid>
      {!isCommissionPerProduct && (
      <Grid
        alignItems="center"
        display="flex"
        size={{
          xs: 4, lg: 2, md: 2, sm: 2,
        }}
      >
        <Typography color="primary">{t('perSale', { ns: 'commissions' })}</Typography>
      </Grid>
      )}
      {!isCommissionPerProduct && (
      <Grid
        size={{
          xs: 12, lg: 12, md: 12, sm: 12,
        }}
      >
        <Divider sx={{ borderStyle: 'dashed' }} />
      </Grid>
      )}
      {!isCommissionPerProduct && (
      <Grid
        alignItems="center"
        display="flex"
        size={{
          xs: 12, lg: 12, md: 12, sm: 12,
        }}
      >
        <Typography variant="overline">
          {t('example', { ns: 'commissions' })}
        </Typography>
        <Typography pl={1} variant="caption">
          Plan
          {' '}
          {Number(selectedCommissionPlan.fixedType) + 1}
        </Typography>
      </Grid>
      )}
      {!isCommissionPerProduct && (
      <Grid
        display="flex"
        size={{
          xs: 12, lg: 12, md: 12, sm: 12,
        }}
      >
        <Typography variant="caption">
          {selectedCommissionPlan.fixedType === 1
            ? t('fixedTenOnEachSale', { ns: 'commissions' })
            : t('flatTenFeeOnEach', { ns: 'commissions' })}
        </Typography>
      </Grid>
      )}
      {!isCommissionPerProduct && (
      <Grid
        display="flex"
        size={{
          xs: 12, lg: 12, md: 12, sm: 12,
        }}
      >
        <Typography variant="caption">
          <Typography variant="overline">
            {t('thisTranslatesTo', { ns: 'commissions' })}
          </Typography>
          {' '}
          <br />
          {selectedCommissionPlan.fixedType === 0 ? (
            <>
              {t('theEmployeeHasMadeFive', { ns: 'commissions' })}
              <br />
              {t('theEmployeeHasMade', { ns: 'commissions' })}
            </>
          ) : t('theEmployeeSoldFor', { ns: 'commissions' })}
        </Typography>
      </Grid>
      )}
    </Grid>
  );
};

export {
  CreateFixedInput,
};
